import { FieldGrid, SegmentedControl, StateBlock } from "@/components/layout-2/ds";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import type { HtmlPreviewProps } from "@/types/dynamic-detail";
import { useEffect, useRef, useState } from "react";

/**
 * Previews an HTML document built by the server in several variants (an email in each brand),
 * picked from a dropdown, at several widths. The document is shown in a sandboxed iframe with no
 * script, scaled down when the column is narrower than the chosen width. With a `highlightLabel`, a
 * switch (on by default) shows the variant's `highlightedHtml`: the same document, its variable parts highlighted.
 */
export function HtmlPreview({ title, variantLabel, highlightLabel, variants, widths }: HtmlPreviewProps) {
    const [variantKey, setVariantKey] = useState(variants[0]?.key ?? "");
    const [width, setWidth] = useState(String(widths[0]?.value ?? 600));
    const [height, setHeight] = useState(600);
    const [highlighted, setHighlighted] = useState(true);
    const [available, setAvailable] = useState(0);
    const stageRef = useRef<HTMLDivElement>(null);
    const frameRef = useRef<HTMLIFrameElement>(null);
    const variant = variants.find(item => item.key === variantKey) ?? variants[0];
    const frameWidth = Number(width);
    const scale = available > 0 ? Math.min(1, available / frameWidth) : 1;
    const canHighlight = Boolean(highlightLabel && variant?.highlightedHtml);
    const html = canHighlight && highlighted ? variant?.highlightedHtml : variant?.html;

    useEffect(() => {
        const stage = stageRef.current;
        if (!stage) return;
        const observer = new ResizeObserver(entries => setAvailable(entries[0].contentRect.width));
        observer.observe(stage);
        return () => observer.disconnect();
    }, []);

    const measure = () => {
        const body = frameRef.current?.contentDocument?.body;
        if (body) setHeight(Math.max(200, Math.ceil(body.getBoundingClientRect().height)));
    };

    return (
        <div className="flex w-full flex-col gap-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-md font-bold">{title}</span>
                {widths.length > 1 && (
                    <SegmentedControl ariaLabel="Width" value={width} options={widths.map(item => ({ value: String(item.value), label: item.label }))} onChange={value => setWidth(value)} />
                )}
            </div>
            {variants.length > 1 && (
                <div className="flex flex-col gap-1">
                    <span className="text-xs text-gray-600">{variantLabel}</span>
                    <Select value={variant?.key} onValueChange={setVariantKey}>
                        <SelectTrigger aria-label={variantLabel}>
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {variants.map(item => (
                                <SelectItem key={item.key} value={item.key}>
                                    {item.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            )}
            {!variant ? (
                <StateBlock message="Nothing to preview." />
            ) : variant.error ? (
                <StateBlock kind="error" message={variant.error} />
            ) : (
                <>
                    {variant.meta && variant.meta.length > 0 && <FieldGrid columns={1} items={variant.meta.map(item => ({ label: item.label, value: item.value }))} />}
                    {canHighlight && (
                        <label className="flex items-center gap-2 text-sm">
                            <Switch checked={highlighted} onCheckedChange={setHighlighted} />
                            {highlightLabel}
                        </label>
                    )}
                    <div ref={stageRef} className="w-full overflow-hidden rounded-sm border bg-gray-50 p-2">
                        <div className="mx-auto overflow-hidden" style={{ width: frameWidth * scale, height: height * scale }}>
                            <iframe
                                ref={frameRef}
                                title={`${title} · ${variant.label}`}
                                srcDoc={html}
                                sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
                                onLoad={measure}
                                scrolling="no"
                                className="block border-0 bg-white"
                                style={{ width: frameWidth, height, transform: `scale(${scale})`, transformOrigin: "top left" }}
                            />
                        </div>
                    </div>
                    {variant.footnote && <span className="text-xs text-gray-500">{variant.footnote}</span>}
                </>
            )}
        </div>
    );
}
